import React, { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { downloadInvalidScansByCountry, downloadUploadLogsReport, downloadValidScansByScore } from '../components/services/reportApi';
import ReportSection from './utils/ReportSection';

const ScoreReportsDashboard = () => {
  const [selectedWeek, setSelectedWeek] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [frequency, setFrequency] = useState('weekly'); // weekly | monthly | custom
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [loading, setLoading] = useState({});
  const [error, setError] = useState({});

  // ---------- Utilities ----------
  // const getPreviousWeek = () => {
  //   const today = new Date();
  //   const dayOfWeek = today.getDay();
  //   const diff = dayOfWeek === 0 ? 7 : dayOfWeek;
  //   const lastSunday = new Date(today);
  //   lastSunday.setDate(today.getDate() - diff);
  //   const lastMonday = new Date(lastSunday);
  //   lastMonday.setDate(lastSunday.getDate() - 6);

  //   return {
  //     start: lastMonday.toISOString().split('T')[0],
  //     end: lastSunday.toISOString().split('T')[0]
  //   };
  // };

  // const getPreviousMonth = () => {
  //   const today = new Date();
  //   const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  //   const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);

  //   return {
  //     start: firstDay.toISOString().split('T')[0],
  //     end: lastDay.toISOString().split('T')[0],
  //     month: firstDay.toISOString().slice(0, 7)
  //   };
  // };

  const getCurrentWeek = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? 7 : dayOfWeek;
    const currentStartDate = new Date(today);
    currentStartDate.setDate(today.getDate() - diff);
    const currentStartStr = currentStartDate.toISOString().split('T')[0];
    const currentEndStr = today.toISOString().split('T')[0];
    return `${currentStartStr}_${currentEndStr}`;
  };

  const getCurrentMonth = () => {
    const today = new Date();
    return today.toISOString().slice(0, 7);
  };

  useEffect(() => {
    const currWeek = getCurrentWeek();
    const currMonth = getCurrentMonth();
    setSelectedWeek(currWeek);
    setSelectedMonth(currMonth);
  }, []);

  // useEffect(() => {
  //   const prevWeek = getPreviousWeek();
  //   const prevMonth = getPreviousMonth();
  //   setSelectedWeek(`${prevWeek.start}_${prevWeek.end}`);
  //   setSelectedMonth(prevMonth.month);
  // }, []);

  // ---------- Reports ----------
  const validScansByScoreReport = async (startDate, endDate, frequency) => {
    await downloadValidScansByScore({ startDate, endDate, frequency });
  };

  const invalidScansByCountryReport = async (startDate, endDate, frequency) => {
    await downloadInvalidScansByCountry({ startDate, endDate, frequency });
  };

  const uploadLogsToS3ByEpn = async (startDate, endDate, frequency) => {
    await downloadUploadLogsReport({ startDate, endDate, frequency });
  };

  // ---------- Download Handler ----------
  const handleDownload = async (reportType) => {
    const reportKey = `${reportType}_${frequency}`;
    setLoading(prev => ({ ...prev, [reportKey]: true }));
    setError(prev => ({ ...prev, [reportKey]: null }));

    try {
      let startDate, endDate;

      if (frequency === 'weekly') {
        if (!selectedWeek) throw new Error('Please select a week');
        [startDate, endDate] = selectedWeek.split('_');
      } else if (frequency === 'monthly') {
        if (!selectedMonth) throw new Error('Please select a month');
        const [year, month] = selectedMonth.split('-');
        startDate = `${year}-${month}-01`;
        const lastDay = new Date(year, month, 0).getDate();
        endDate = `${year}-${month}-${lastDay.toString().padStart(2, '0')}`;
      } else {
        if (!customStart || !customEnd) throw new Error('Please enter custom start and end dates');
        startDate = customStart;
        endDate = customEnd;
      }

      if (reportType === 'valid-scans-by-score') {
        await validScansByScoreReport(startDate, endDate, frequency);
      } else if (reportType === 'invalid-scans-by-country'){
        await invalidScansByCountryReport(startDate, endDate, frequency);
      } else {
        await uploadLogsToS3ByEpn(startDate, endDate, frequency);
      }
    } catch (err) {
      setError(prev => ({ ...prev, [reportKey]: err.message }));
      console.error('Download error:', err);
    } finally {
      setLoading(prev => ({ ...prev, [reportKey]: false }));
    }
  };

  // // ---------- Option Generators ----------
  // const getWeekOptions = () => {
  //   const options = [];
  //   const today = new Date();

  //   for (let i = 1; i <= 12; i++) {
  //     const endDate = new Date(today);
  //     const dayOfWeek = today.getDay();
  //     const diff = dayOfWeek === 0 ? 7 : dayOfWeek;
  //     endDate.setDate(today.getDate() - diff - ((i - 1) * 7));

  //     const startDate = new Date(endDate);
  //     startDate.setDate(endDate.getDate() - 6);

  //     const startStr = startDate.toISOString().split('T')[0];
  //     const endStr = endDate.toISOString().split('T')[0];

  //     options.push({
  //       value: `${startStr}_${endStr}`,
  //       label: `${startStr} to ${endStr}`
  //     });
  //   }

  //   return options;
  // };

  // const getMonthOptions = () => {
  //   const options = [];
  //   const today = new Date();

  //   for (let i = 1; i <= 12; i++) {
  //     const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
  //     const monthStr = date.toISOString().slice(0, 7);
  //     const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  //     options.push({
  //       value: monthStr,
  //       label: monthName
  //     });
  //   }

  //   return options;
  // };

  // ---------- Option Generators ----------
  const getWeekOptions = () => {
    const options = [];
    const today = new Date();

    // Add current week option
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? 7 : dayOfWeek;
    const currentEndDate = today;
    const currentStartDate = new Date(today);
    currentStartDate.setDate(today.getDate() - diff);

    const currentStartStr = currentStartDate.toISOString().split('T')[0];
    const currentEndStr = currentEndDate.toISOString().split('T')[0];

    options.push({
      value: `${currentStartStr}_${currentEndStr}`,
      label: `${currentStartStr} to ${currentEndStr}  (Current Week)`
    });

    // Add past 12 weeks
    for (let i = 1; i <= 12; i++) {
      const endDate = new Date(today);
      const diff = dayOfWeek === 0 ? 7 : dayOfWeek;
      endDate.setDate(today.getDate() - diff - ((i - 1) * 7));

      const startDate = new Date(endDate);
      startDate.setDate(endDate.getDate() - 6);

      const startStr = startDate.toISOString().split('T')[0];
      const endStr = endDate.toISOString().split('T')[0];

      options.push({
        value: `${startStr}_${endStr}`,
        label: `${startStr} to ${endStr}`
      });
    }

    return options;
  };

  const getMonthOptions = () => {
    const options = [];
    const today = new Date();

    // Add current month option
    const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const currentMonthStr = currentMonthStart.toISOString().slice(0, 7);
    const currentMonthName = currentMonthStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    options.push({
      value: currentMonthStr,
      label: `${currentMonthName} (Current Month)`
    });

    // Add past 12 months
    for (let i = 1; i <= 12; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthStr = date.toISOString().slice(0, 7);
      const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

      options.push({
        value: monthStr,
        label: monthName
      });
    }

    return options;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Reports Download Dashboard</h1>
          <p className="text-gray-600">Download weekly, monthly, or custom scan reports</p>
        </div>

        {/* --- Report Time Period Section --- */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Report Time Period</h2>

          {/* Frequency Selector */}
          <div className="relative flex w-full flex-col rounded-xl bg-white shadow mb-6">
            <nav className="flex flex-row gap-4 p-2 flex-nowrap justify-start">
              {/* Weekly */}
              <div
                role="button"
                className="flex items-center rounded-lg p-0 transition-all hover:bg-slate-100 focus:bg-slate-100 active:bg-slate-100"
              >
                <label
                  htmlFor="weekly-frequency"
                  className="flex cursor-pointer items-center px-2 py-1 whitespace-nowrap"
                >
                  <div className="inline-flex items-center">
                    <label className="relative flex items-center cursor-pointer" htmlFor="weekly-frequency">
                      <input
                        name="frequency"
                        type="radio"
                        id="weekly-frequency"
                        value="weekly"
                        checked={frequency === 'weekly'}
                        onChange={(e) => setFrequency(e.target.value)}
                        className="peer h-4 w-4 cursor-pointer appearance-none rounded-full border border-slate-300 checked:border-slate-400 transition-all"
                      />
                      <span className="absolute bg-slate-800 w-2.5 h-2.5 rounded-full opacity-0 peer-checked:opacity-100 transition-opacity duration-200 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></span>
                    </label>
                    <label className="ml-2 text-slate-600 cursor-pointer text-sm" htmlFor="weekly-frequency">
                      Weekly
                    </label>
                  </div>
                </label>
              </div>

              {/* Monthly */}
              <div
                role="button"
                className="flex items-center rounded-lg p-0 transition-all hover:bg-slate-100 focus:bg-slate-100 active:bg-slate-100"
              >
                <label
                  htmlFor="monthly-frequency"
                  className="flex cursor-pointer items-center px-2 py-1 whitespace-nowrap"
                >
                  <div className="inline-flex items-center">
                    <label className="relative flex items-center cursor-pointer" htmlFor="monthly-frequency">
                      <input
                        name="frequency"
                        type="radio"
                        id="monthly-frequency"
                        value="monthly"
                        checked={frequency === 'monthly'}
                        onChange={(e) => setFrequency(e.target.value)}
                        className="peer h-4 w-4 cursor-pointer appearance-none rounded-full border border-slate-300 checked:border-slate-400 transition-all"
                      />
                      <span className="absolute bg-slate-800 w-2.5 h-2.5 rounded-full opacity-0 peer-checked:opacity-100 transition-opacity duration-200 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></span>
                    </label>
                    <label className="ml-2 text-slate-600 cursor-pointer text-sm" htmlFor="monthly-frequency">
                      Monthly
                    </label>
                  </div>
                </label>
              </div>

              {/* Custom Dates */}
              <div
                role="button"
                className="flex items-center rounded-lg p-0 transition-all hover:bg-slate-100 focus:bg-slate-100 active:bg-slate-100"
              >
                <label
                  htmlFor="custom-frequency"
                  className="flex cursor-pointer items-center px-2 py-1 whitespace-nowrap"
                >
                  <div className="inline-flex items-center">
                    <label className="relative flex items-center cursor-pointer" htmlFor="custom-frequency">
                      <input
                        name="frequency"
                        type="radio"
                        id="custom-frequency"
                        value="custom"
                        checked={frequency === 'custom'}
                        onChange={(e) => setFrequency(e.target.value)}
                        className="peer h-4 w-4 cursor-pointer appearance-none rounded-full border border-slate-300 checked:border-slate-400 transition-all"
                      />
                      <span className="absolute bg-slate-800 w-2.5 h-2.5 rounded-full opacity-0 peer-checked:opacity-100 transition-opacity duration-200 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></span>
                    </label>
                    <label className="ml-2 text-slate-600 cursor-pointer text-sm whitespace-nowrap" htmlFor="custom-frequency">
                      Custom Dates
                    </label>
                  </div>
                </label>
              </div>
            </nav>
          </div>

          {/* Dynamic Inputs */}
          {frequency === 'weekly' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Weekly Period
              </label>
              <select
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {getWeekOptions().map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          )}

          {frequency === 'monthly' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Monthly Period
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {getMonthOptions().map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          )}

          {frequency === 'custom' && (
            <div className="mt-2">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Custom Date Range
              </label>
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-sm text-gray-600 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex-1">
                  <label className="block text-sm text-gray-600 mb-1">End Date</label>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

        </div>

        {/* --- Reports Section --- */}
        <div className="space-y-6">
          <ReportSection
            title="Valid Scans by Score Report"
            description="Number of times a valid score report was scanned for the given time period"
            reportType="valid-scans-by-score"
            selectedWeek={selectedWeek}
            selectedMonth={selectedMonth}
            loading={loading}
            error={error}
            onDownload={() => handleDownload('valid-scans-by-score')}
          />

          <ReportSection
            title="Invalid Scans by Country"
            description="Number of invalid score reports scanned in each country for the given time period"
            reportType="invalid-scans-by-country"
            selectedWeek={selectedWeek}
            selectedMonth={selectedMonth}
            loading={loading}
            error={error}
            onDownload={() => handleDownload('invalid-scans-by-country')}
          />

          <ReportSection
            title="Data Upload Logs"
            description="Number of score reports uploaded by EPN for the given time period"
            reportType="upload-logs-to-s3"
            selectedWeek={selectedWeek}
            selectedMonth={selectedMonth}
            loading={loading}
            error={error}
            onDownload={() => handleDownload('upload-logs-to-s3')}
          />
        </div>
      </div>
    </div>
  );
};

export default ScoreReportsDashboard;
